import {
  formatRegistroAlteradoTipo,
  resolveAuditJustificativa,
  getAuditModuleLabel,
  resolveAuditValues,
} from "@/modules/audit";
import type { AuditDetailStatus, AuditRecord } from "@/modules/audit";
import { formatUserPhone } from "@/modules/users";
import { formatAuditDateTime } from "@/shared/components/audit-date-time/audit-date-time";
import { isEmail } from "@/shared/components/user-avatar";
import { formatAuditDetalhes, parseAuditPares } from "../../audit.utils";
import { useAuditRecordDetail } from "../../hooks/use-audit-record-detail";
import {
  AUDIT_DETAIL_EMPTY_FIELD,
  AUDIT_DETAIL_LOADING_MESSAGE,
  AUDIT_DETAIL_REAUTH_LABEL,
  AUDIT_DETAIL_REGISTRO_FALLBACK_LABEL,
  AUDIT_DETAIL_RETRY_LABEL,
} from "./audit-detail-dialog.constants";
import type {
  AuditDetailCampos,
  AuditDetailDialogProps,
  AuditDetailDialogViewModel,
  AuditDetailEstado,
} from "./types-audit-detail-dialog";

/** Novo valor em duas ou mais linhas, todas `Rótulo: valor`; qualquer outro texto fica como veio (`null`). */
function parseAtributos(valor: string): AuditDetailCampos["valorNovoAtributos"] {
  const linhas = valor.split("\n");
  if (linhas.length < 2) return null;

  const atributos = linhas.map((linha) => /^([^:]+:)\s(.*)$/.exec(linha));
  if (atributos.some((atributo) => atributo === null)) return null;

  return atributos.map((atributo) => ({ rotulo: atributo![1]!, valor: atributo![2]! }));
}

function buildCampos(record: AuditRecord): AuditDetailCampos {
  const values = resolveAuditValues(record);
  const detalhes = formatAuditDetalhes(values.anterior, values.novo);
  const justificativa = resolveAuditJustificativa(record);
  const registroDetalhe = record.registroAlteradoEmail || record.registroAlteradoCodigo;
  const emailDaFoto = [record.responsavelEmail, record.responsavel].find(isEmail)?.trim() ?? null;

  return {
    acao: record.acaoDescricao || record.acao || AUDIT_DETAIL_EMPTY_FIELD,
    registroAlterado: record.registroAlteradoNome
      ? {
          label: record.registroAlteradoTipo
            ? formatRegistroAlteradoTipo(record.registroAlteradoTipo)
            : AUDIT_DETAIL_REGISTRO_FALLBACK_LABEL,
          valor: registroDetalhe
            ? `${record.registroAlteradoNome} (${registroDetalhe})`
            : record.registroAlteradoNome,
        }
      : null,
    obra:
      record.registroAlteradoNome && record.registroAlteradoObra
        ? record.registroAlteradoObra
        : null,
    // Com o detalhe em linhas, os valores crus (JSON) não aparecem.
    valorAnterior: detalhes ? "" : values.anterior,
    valorAnteriorRiscado: values.anteriorRiscado,
    valorNovo: detalhes ? "" : values.novo,
    valorNovoAtributos: detalhes ? null : parseAtributos(values.novo),
    detalhes,
    justificativa,
    justificativaPares: parseAuditPares(justificativa),
    responsavel: {
      nome: record.responsavel || AUDIT_DETAIL_EMPTY_FIELD,
      emailDaFoto,
      email: record.responsavelEmail,
      telefone: formatUserPhone(record.responsavelTelefone),
      cargo: record.responsavelDetalhe,
    },
    dataHora: formatDataHora(record.dataHora),
    modulo: getAuditModuleLabel(record),
  };
}

/** Data e hora do evento na mesma linha, como no Figma. */
function formatDataHora(value: string): string {
  if (!value) return AUDIT_DETAIL_EMPTY_FIELD;

  const { date, time } = formatAuditDateTime(value);

  return time ? `${date} ${time}` : date;
}

type Falha = Exclude<AuditDetailStatus, "success">;

function buildEstado(
  status: Falha,
  errorMessage: string | null,
  { onReauthenticate }: Pick<AuditDetailDialogProps, "onReauthenticate">,
  reload: () => void,
): AuditDetailEstado {
  const testId = `audit-detail-${status}`;
  const title = status === "loading" ? AUDIT_DETAIL_LOADING_MESSAGE : (errorMessage ?? "");

  switch (status) {
    case "loading":
      return { testId, variant: "loading", title };
    case "sessionExpired":
      return {
        testId,
        variant: "forbidden",
        title,
        action: { label: AUDIT_DETAIL_REAUTH_LABEL, onClick: onReauthenticate },
      };
    case "forbidden":
      return { testId, variant: "forbidden", title };
    case "notFound":
      return { testId, variant: "empty", title };
    case "error":
      return {
        testId,
        variant: "error",
        title,
        action: { label: AUDIT_DETAIL_RETRY_LABEL, onClick: reload, icon: "refresh" },
      };
  }
}

/**
 * Detalhe somente leitura de um registro de auditoria (HU 834): o registro é consultado ao abrir (RN001) e nenhuma
 * escrita sai daqui (RN004/RN005). Cada falha tem estado próprio e nenhuma exibe dados do registro.
 */
export function useAuditDetailDialog({
  recordId,
  onClose,
  onReauthenticate,
}: AuditDetailDialogProps): AuditDetailDialogViewModel {
  const { status, record, errorMessage, reload } = useAuditRecordDetail(recordId);

  return {
    estado:
      status === "success" ? null : buildEstado(status, errorMessage, { onReauthenticate }, reload),
    campos: record ? buildCampos(record) : null,
    fechar: onClose,
  };
}
